import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { UserActions } from './index';
import { USER_ROLES } from '../../services/api.service';

export const userFeatureKey = 'user';
export interface UserState {
  loading: boolean;
  id: string;
  avatar: string;
  name: string;
  email: string;
  claims: any;
  roles: string[],
  onBoardingRole: string,
  incognito: boolean,
  public: boolean,
  publicLinkId: string,
  score: number;
  chatIdentifier: string,
  systemChatId: string,
  profile: any;
  jobTitles: any;
  hardSkillsList: any;
  hardSkills: any;
  softSkills: any;
  messagesCount: {
    messages: number;
    notify: number;
    applies: number;
  },
  lastUpdatedThread: string;
  files: any[];
  defaultCv: {
    // publicId: string;
    fileUrl: string;
    type: string;
  },
  telegramToken: string;
  skillsValidation: any;
  accountId: string;
}

const initialState: UserState = {
  loading: false,
  id: null,
  avatar: null,
  name: null,
  email: null,
  claims : null,
  roles: [],
  onBoardingRole: null,
  incognito: null,
  public: null,
  publicLinkId: null,
  score: null,
  chatIdentifier: null,
  systemChatId: null,
  profile: null,
  jobTitles: null,
  hardSkillsList: null,
  hardSkills: null,
  softSkills: null,
  messagesCount: {
    messages: null,
    notify: 0,
    applies: 0
  },
  lastUpdatedThread: null,
  files: [],
  defaultCv: null,
  telegramToken: null,
  skillsValidation: null,
  accountId: null,
};

const reducer = createReducer<UserState>(
  initialState,
  on(UserActions.GetUserSuccess, (state, action) => ({
    ...state, ...action.payload
  })),
  on(UserActions.SetUserRoleSuccess, (state, action) => ({
    ...state, onBoardingRole: action.role
  })),
  on(UserActions.SetUserPreData, (state, action) => ({
    ...state,
    id: action.id,
    email: action.email,
    name: action.name,
    onBoardingRole: action.onBoardingRole,
    files: action.files
  })),
  on(UserActions.SetUserAvatarSuccess, (state, action) => ({
    ...state, avatar: action.avatar
  })),
  on(UserActions.SetUserName, (state, action) => ({
    ...state, name: action.name
  })),
  on(UserActions.SetUserEmail, (state, action) => ({
    ...state, email: action.email
  })),
  on(UserActions.SetUserIncognito, (state, action) => ({
    ...state, incognito: action.incognito
  })),
  on(UserActions.SetUserPublic, (state, action) => ({
    ...state, public: action.public
  })),
  on(UserActions.SetUserScore, (state, action) => ({
    ...state, score: action.score
  })),
  on(UserActions.SetTalentDetails, (state, action) => ({
    ...state,
    profile: {
      ...(state.profile || {}),
      details: {
        ...((state.profile && state.profile.details) || {}),
        ...action.details
      }
    }
  })),
  on(UserActions.SetTalentSenioritySuccess, (state, action) => ({
    ...state,
    profile: {
      ...state.profile,
      details: {
        ...state.profile.details,
        jobTitleLevel: action.level
      }
    }
  })),
  on(UserActions.SetTalentExpectations, (state, action) => ({
    ...state,
    profile: { ...state.profile, expectations: action.expectations }
  })),
  on(UserActions.SetTalentContacts, (state, action) => ({
    ...state,
    profile: { ...state.profile, contacts: action.contacts }
  })),
  on(UserActions.TalentDefaultCv, (state, action) => ({ ...state, defaultCv: action.payload })),
  on(UserActions.SetTalentTelegram, (state, action) => ({
    ...state,
    profile: {
      ...state.profile,
      contacts: {
        ...state.profile.contacts,
        telegram: action.telegram
      }
    }
  })),
  on(UserActions.GetTalentSkillsSuccess, (state, action) => ({
    ...state,
    hardSkills: action.hardSkills,
    softSkills: action.softSkills
  })),
  on(UserActions.SetHardSkills, (state, action) => ({
    ...state,
    hardSkills: action.skillsList
  })),
  on(UserActions.SetSoftSkills, (state, action) => ({
    ...state,
    softSkills: action.skillsList
  })),
  on(UserActions.GetSkillsListSuccess, (state, action) => ({
    ...state,
    hardSkillsList: action.skillsList
  })),
  on(UserActions.GetJobTitlesSuccess, (state, action) => ({
    ...state,
    jobTitles: action.jobTitles
  })),
  on(UserActions.SetHmAboutYou, (state, action) => ({
    ...state,
    profile: { ...state.profile, aboutYou: action.aboutYou }
  })),
  on(UserActions.SetHmTelegram, (state, action) => ({
    ...state,
    profile: {
      ...state.profile,
      aboutYou: {
        ...state.profile.aboutYou,
        telegram: action.telegram
      }
    }
  })),
  on(UserActions.SetHmCompany, (state, action) => ({
    ...state,
    profile: { ...state.profile, company: action.company }
  })),
  on(UserActions.SetCompanyLogoSuccess, (state, action) => ({
    ...state,
    profile: {
      ...state.profile,
      company: {
        ...state.profile.company,
        logo: action.logo
      }
    }
  })),
  on(UserActions.SetHmFinance, (state, action) => ({
    ...state,
    profile: { ...state.profile, finance: action.finance }
  })),
  on(UserActions.SetMessageCount, (state, action) => ({
    ...state,
    messagesCount: {
      ...state.messagesCount,
      messages: action.count
    }
  })),
  on(UserActions.SetNotifyCount, (state, action) => ({
    ...state,
    messagesCount: {
      ...state.messagesCount,
      notify: action.count
    }
  })),
  on(UserActions.SetAppliesCount, (state, action) => ({
    ...state,
    messagesCount: {
      ...state.messagesCount,
      applies: action.count
    }
  })),
  on(UserActions.SetLastTreadUpdated, (state, action) => ({
    ...state,
    lastUpdatedThread: action.threadId
  })),
  on(UserActions.GetSkillsValidationSuccess, (state, action) => ({
    ...state,
    skillsValidation: action.payload
  }))
);

export function userReducer(state: UserState | undefined, action: Action) {
  return reducer(state, action);
}

export const selectUserState = createFeatureSelector<UserState>(userFeatureKey);

export const selectIsAdmin = createSelector(
  selectUserState,
  (state) => state?.roles?.includes('admin') || false
);

export const selectIsAccountAdmin = createSelector(
  selectUserState,
  (state) => state?.roles?.includes(USER_ROLES.accountAdmin) || false
);

export const selectUserLoading = createSelector(
  selectUserState,
  (state) => state.loading
);

export const selectUserId = createSelector(
  selectUserState,
  (state) => state.id
);

export const selectUserRole = createSelector(
  selectUserState,
  (state) => state.roles && state.roles.length ? state.roles[0] : null
);

export const selectOnboardingUserRole = createSelector(
  selectUserState,
  (state) => state.onBoardingRole
);

export const selectUserAvatar = createSelector(
  selectUserState,
  (state) => state.avatar
);

export const selectUserName = createSelector(
  selectUserState,
  (state) => state.name
);

export const selectUserEmail = createSelector(
  selectUserState,
  (state) => state.email
);

export const selectUserIncognito = createSelector(
  selectUserState,
  (state) => state.incognito
);

export const selectUserPublic = createSelector(
  selectUserState,
  (state) => state.public
);

export const selectUserPublicLinkId = createSelector(
  selectUserState,
  (state) => state.publicLinkId
);

export const selectUserScore = createSelector(
  selectUserState,
  (state) => state.score
);

export const selectTalentJobTitles = createSelector(
  selectUserState,
  (state) => state.jobTitles ? state.jobTitles : []
);

export const selectTalentSelectedJobTitles = createSelector(
  selectUserState,
    (state) => state.profile && state.profile.details && state.profile.details.jobTitles ? state.profile.details.jobTitles : []
);

export const selectTalentHardSkillsList = createSelector(
  selectUserState,
  (state) => state.hardSkillsList ? state.hardSkillsList : []
);

export const selectTalentHardSkills = createSelector(
  selectUserState,
  (state) => state.hardSkills ? state.hardSkills : []
);

export const selectTalentSoftSkills = createSelector(
  selectUserState,
  (state) => state.softSkills ? state.softSkills : []
);

export const selectTalentExpectations = createSelector(
  selectUserState,
  (state) => state.profile && state.profile.expectations ? state.profile.expectations : null
);

export const selectTalentContacts = createSelector(
  selectUserState,
  (state) => state.profile && state.profile.contacts ? state.profile.contacts : null
);

export const selectTalentDetails = createSelector(
  selectUserState,
  (state) => state.profile && state.profile.details ? state.profile.details : null
);

export const selectTalentJobTitle = createSelector(
  selectUserState,
  (state) => state.profile && state.profile.details && state.profile.details.jobTitle ? state.profile.details.jobTitle : null
);

export const selectTalentJobTitleLevel = createSelector(
  selectUserState,
  (state) => state.profile && state.profile.details && state.profile.details.jobTitleLevel ? state.profile.details.jobTitleLevel : null
);

export const selectHmAboutYou = createSelector(
  selectUserState,
  (state) => state.profile && state.profile.aboutYou ? state.profile.aboutYou : null
);

export const selectHmCompany = createSelector(
  selectUserState,
  (state) => state.profile && state.profile.company ? state.profile.company : null
);

export const selectHmCompanyId = createSelector(
  selectUserState,
  (state) => state.profile && state.profile.company && state.profile.company.id ? state.profile.company.id : null
);

export const selectHmCompanyLogo = createSelector(
  selectUserState,
  (state) => state.profile && state.profile.company && state.profile.company.logo ? state.profile.company.logo : null
);

export const selectHmFinance = createSelector(
  selectUserState,
  (state) => state.profile && state.profile.finance ? state.profile.finance : null
);

export const selectUserChatId = createSelector(
  selectUserState,
  (state) => state.chatIdentifier
);

export const selectUserSystemChatId = createSelector(
  selectUserState,
  (state) => state.systemChatId
);

export const selectMessageCount = createSelector(
  selectUserState,
  (state) => state.messagesCount.messages
);

export const selectNotifyCount = createSelector(
  selectUserState,
  (state) => state.messagesCount.notify
);

export const selectAppliesCount = createSelector(
  selectUserState,
  (state) => state.messagesCount.applies
);

export const selectLastUpdatedThreadId = createSelector(
  selectUserState,
  (state) => state.lastUpdatedThread
);

export const selectFiles = createSelector(
  selectUserState,
  (state) => state.files
);

export const selectDefaultCv = createSelector(
  selectUserState,
  (state) => state.defaultCv
);

export const selectTelegramToken = createSelector(
  selectUserState,
  (state) => state.telegramToken
);

export const selectSkillsValidation = createSelector(
  selectUserState,
  (state) => state.skillsValidation
);

export const selectSalaryRangeParams = createSelector(
  selectUserState,
  (state) => ({
    country: state?.profile?.details?.country ? state.profile.details.country : null,
    position: state?.profile?.details?.jobTitle ? state.profile.details.jobTitle : null,
    seniority: state?.profile?.details?.jobTitleLevel ? state.profile.details.jobTitleLevel : null,
    currency: state?.profile?.expectations?.salaryCurrency ? state.profile.expectations.salaryCurrency : null
  })
);

export const selectUserClaims = createSelector(
  selectUserState,
  (state) => state.claims
);

export const selectUserAccountId = createSelector(
  selectUserState,
  (state) => state.accountId
);

export const selectUserRoles = createSelector(
  selectUserState,
  (state) => state.roles || []
);
